// Dates and labels, in one place, so every screen prints them the same way.

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "18 Sep" */
export const fmtShort = (d: string) => `${+d.slice(8, 10)} ${MON[+d.slice(5, 7) - 1]}`;

/** "18 Sep 2026" */
export const fmtLong = (d: string) => `${+d.slice(8, 10)} ${MON[+d.slice(5, 7) - 1]} ${d.slice(0, 4)}`;

/** "16–24 Sep 2026", "31 Aug – 16 Sep 2026", "28 Dec 2025 – 8 Jan 2026", or one day: the dates a page of
 *  the phone's grid holds, earliest first */
export function fmtRange(a: string, b: string): string {
  if (a > b) [a, b] = [b, a];
  if (a === b) return fmtLong(a);
  if (a.slice(0, 7) === b.slice(0, 7)) return `${+a.slice(8, 10)}–${fmtLong(b)}`;
  if (a.slice(0, 4) === b.slice(0, 4)) return `${fmtShort(a)} – ${fmtLong(b)}`;
  return `${fmtLong(a)} – ${fmtLong(b)}`;
}

/** "Sep ’26" — the month ruler's label */
export const monthLabel = (d: string) => `${MON[+d.slice(5, 7) - 1]} ’${d.slice(2, 4)}`;

/** "21 Sep 2026, 4:12 a.m. ET" — the Last updated line, in the record's own clock */
export function fmtUpdated(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const ap = (p.dayPeriod || '').toLowerCase().replace('am', 'a.m.').replace('pm', 'p.m.');
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} ${ap} ET`;
}

/** "Oct 2023", from "2023-10" */
export const fmtMonth = (m: string) => `${MON[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}`;

/** The leadership post a member held in the term shown (worker grid.leaders): the Speaker, from
 *  the House Clerk's roll calls, and the Senate's Majority Leader, from the Senate's own list.
 *  The long words, with "from" or "until" a month only when the post changed hands during the
 *  term, go on the card and the name's hover; the short ones under the name in the grid, where
 *  the dates do not fit (Dan, 24 Sep 2026). The share card and the link preview use the same long
 *  words (worker cards.py, server pages.py, held to these by their checks). */
export const LEADER = {
  speaker: { long: 'Speaker of the House', short: 'Speaker' },
  majority_leader: { long: 'Majority Leader', short: 'Majority Leader' },
};
export interface Leadership { role: keyof typeof LEADER; from: string | null; until: string | null }
export function leaderWords(l: Leadership | null | undefined): string | null {
  const w = l ? LEADER[l.role] : undefined;
  if (!l || !w) return null;
  return w.long + (l.from ? ' from ' + fmtMonth(l.from) : '') + (l.until ? ' until ' + fmtMonth(l.until) : '');
}
/** A post's short words beside a name: dated when its holder handed it on during the term and stayed
 *  (Dan, 15 Oct 2026: McConnell "Majority Leader to Jan 2021", McCarthy "Speaker to Oct 2023"). */
export function leaderShort(l: Leadership | null | undefined): string | null {
  const w = l ? LEADER[l.role] : undefined;
  if (!l || !w) return null;
  return w.short + (l.until ? ' to ' + fmtMonth(l.until) : '');
}

/** A Senate impeachment trial's verdict (the build's column `trial`): the record stores Guilty as Yea. */
export const TRIAL_NOTE = 'On a Senate trial vote, Yea is guilty and Nay is not guilty. Conviction needs two-thirds of the senators present (Constitution, Art. I, Sec. 3).';
export const isTrialVote = (question: string | null | undefined) => /^guilty or not guilty$/i.test((question ?? '').trim());

/** Pinned first in its band and marked: a leader still holding the post at the end of the term
 *  (or today) and still serving (worker grid.pinned). */
export const pinnedLeader = (l: Leadership | null | undefined, left: boolean) => !!l && !l.until && !left;

/** Unexpected Items (T18): the brief's ten kinds of listing, in its order (worker scan.CATEGORIES),
 *  under the brief's labels with Dan's three changes (29 Sep 2026), each because the brief's wording
 *  said more than the text shows. "{kind}" is the item's own kind: the bill's, the resolution's. */
export const UNEXPECTED: Record<string, string> = {
  off_subject: 'Not related to the {kind}’s main subject',
  narrow_beneficiary: 'Applies only to a specific place or group',
  policy_via_number: 'Policy change made through a dollar amount',
  named_funding: 'Funds a specific named project or event',
  place_specific_amount: 'Different amount for specific places or groups',
  selective_carve_out: 'Exception or delay for a subset',
  rule_delay: 'Delays or blocks a specific regulation',
  process_change: 'Changes a government process',
  unnamed_target: 'Condition defined by a narrow set of criteria',
  political_name: 'Program name includes a political figure’s name',
};
/** Check B, comparing a bill's versions: the pop-up's words (Dan reads them before it goes live). */
export const CHANGES = {
  title: (chamber: string) => `Changed after ${chamber} passage`,
  removed: 'Removed before the final vote',
  added: (chamber: string) => `Added after ${chamber} passage`,
  notCarried: 'Not carried',                                              // a removed section, in part
  newInFinal: 'New in the final text',                                     // an added section, in part
};
export const otherChamber = (chamber: string) => (chamber === 'House' ? 'Senate' : 'House');

export const unexpectedLabel = (category: string, kind: string) =>
  (UNEXPECTED[category] ?? category).replace('{kind}', kind);

/** "119th" */
export function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] || 'th';
  return `${n}${s}`;
}

/** Said in place of "Next election" once it has been held (Dan, 12 Oct 2026): a kicker and its value. */
export function afterElection(m: { term_ends?: string; awaiting_successor?: string }): [string, string] | null {
  if (m.term_ends) return ['Term ends', fmtLong(m.term_ends)];
  if (m.awaiting_successor) return ['Serving until', `the winner of the ${fmtLong(m.awaiting_successor)} special election takes the seat`];
  return null;
}

/** Said in place of an office's phone and contact form for someone no longer in office. */
export const CONTACT_FORMER = 'No longer in office, so the office’s phone and contact form are not shown.';

/** The column outcome, in words, from the published code. */
export function outcomeWords(code: string, kind: string): string {
  const resolution = kind.endsWith('resolution');
  switch (code) {
    case 'became_law': return 'Became law';
    case 'law_without_signature': return 'Law without signature';
    case 'vetoed': return 'Vetoed';
    case 'passed': return resolution ? 'Agreed to' : 'Passed';
    // Each chamber passed it, but not the same text (12 Oct 2026 audit: S. 1383, H.R. 3944 ...)
    case 'passed_different': return resolution ? 'Agreed to in two versions' : 'Passed in two versions';
    case 'passed_house': return resolution ? 'Agreed to in House' : 'Passed House';
    case 'passed_senate': return resolution ? 'Agreed to in Senate' : 'Passed Senate';
    case 'failed_house': return 'Failed House';
    case 'failed_senate': return 'Failed Senate';
    case 'confirmed': return 'Confirmed';
    case 'rejected': return 'Rejected';
    default: return code;
  }
}
